import React, { useContext, useCallback, useMemo } from 'react';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { ConfigContext } from '../../context/ConfigContext';
import {
  AggregateConfig,
  RelationalPropertyType,
  ScalarPropertyType,
} from '@terragotech/gen5-config-lib';
import _ from 'lodash';
import { useFormDialog } from '@terragotech/gen5-shared-components';
import { SelectModal } from './SelectModal';
import { JoinModal } from './JoinModal';
import {
  Badge,
  BadgeLabel,
} from 'pages/aggregates/components/CommandFormEditor/MobileTheme/FormBadge';
import { BadgeColors } from 'utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

export const Views: React.FC<{
  view: AggregateConfig['view'];
  setView: React.Dispatch<React.SetStateAction<AggregateConfig['view']>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ view, setView, setIsDirty }) => {
  const classes = useStyles();
  const theme = require('@material-ui/core/styles').useTheme();
  const { getAggregates } = useContext(ConfigContext);
  const aggregateTypes = getAggregates();
  const { aggrUICustomization } = useParams() as { aggrUICustomization: string };

  const formDialog = useFormDialog();

  const aggConfig = useMemo(
    () => aggregateTypes.find((a) => a.typeName === aggrUICustomization)!,
    [aggregateTypes, aggrUICustomization]
  );

  const relationships = useMemo(
    () =>
      aggregateTypes.reduce<any>((prev, agg) => {
        prev[agg.typeName] = Object.keys(agg.properties).reduce<any[]>((p, c) => {
          if ((agg.properties[c] as any).relation === 'ONE-TO-ONE') {
            return p.concat({ relatedRecordType: agg.properties[c].type, relatedColumn: c });
          }
          return p;
        }, []);
        return prev;
      }, {}),
    [aggregateTypes]
  );

  const [selectOptions, joinOptions] = useMemo(() => {
    let attributeNames: string[] =
      aggConfig && aggConfig.properties
        ? Object.keys(aggConfig.properties)
            .filter(
              (k) =>
                !['Hidden', 'JSON', 'Geography'].includes(
                  (aggConfig.properties[k] as ScalarPropertyType).uiType || ''
                )
            )
            .map((p) =>
              (aggConfig.properties[p] as RelationalPropertyType).relation !== 'ONE-TO-MANY'
                ? `"record"."${p}"`
                : `"record"."${p}" (One-To-Many)`
            )
        : [];
    let extraJoins: string[] = [];

    if (view?.joins?.length) {
      for (const join of view.joins) {
        if (join.length) {
          const match = join.match(/\((\w+)\) AS "(\w+)"/)!;
          const recordType = match[1];
          const name = match[2];
          const localSchema = aggregateTypes.find((a) => a.typeName === recordType)!;
          attributeNames = attributeNames.concat(
            Object.keys(localSchema.properties)
              .filter(
                (k) =>
                  !['Hidden', 'JSON', 'Geography', 'Symbol Key'].includes(
                    (localSchema.properties[k] as ScalarPropertyType).uiType || ''
                  )
              )
              .map((n) =>
                (localSchema.properties[n] as RelationalPropertyType).relation !== 'ONE-TO-MANY'
                  ? `"${name}"."${n}" AS "${name}${n}"`
                  : `"${name}"."${n}" AS "${name}${n}" (One-To-Many)`
              )
          );

          extraJoins = extraJoins.concat(
            relationships?.[recordType]?.reduce((prev: any[], curr: any) => {
              return prev.concat(
                `LEFT JOIN ${name}.${curr.relatedColumn} (${curr.relatedRecordType}) AS "${name}LJ${curr.relatedColumn}"`
                //`INNER JOIN ${name}.${curr.relatedColumn} (${curr.relatedRecordType}) AS "${name}IJ${curr.relatedColumn}"`
              );
            }, [])
          );
        }
      }
    }
    const joinOptions: string[] =
      relationships?.[aggrUICustomization]
        ?.reduce((prev: any[], curr: any) => {
          return prev.concat(
            `LEFT JOIN record.${curr.relatedColumn} (${curr.relatedRecordType}) AS "recordLJ${curr.relatedColumn}"`
            //`INNER JOIN record.${curr.relatedColumn} (${curr.relatedRecordType}) AS "recordIJ${curr.relatedColumn}"`
          );
        }, [])
        .concat(...extraJoins) || [];
    return [attributeNames, joinOptions];
  }, [relationships, aggConfig, view, aggrUICustomization, aggregateTypes]);

  const setSelect = useCallback(
    (
      newVal: {
        label: string;
        value: string;
        isLink?: boolean;
      },
      index?: number
    ) => {
      if (index === undefined) {
        setView((prev) => ({ ...prev, selectValues: (prev?.selectValues || []).concat(newVal) }));
      } else {
        setView((prev) => {
          const copy = _.cloneDeep(prev);
          copy!.selectValues![index] = newVal;
          return copy;
        });
      }
      setIsDirty(true);
    },
    [setView, setIsDirty]
  );

  const removeSelect = useCallback(
    (index: number) => {
      setView((prev) => ({
        ...prev,
        selectValues: (prev?.selectValues || []).filter((_, idx) => index !== idx),
      }));
      setIsDirty(true);
    },
    [setView, setIsDirty]
  );
  const removeJoin = useCallback(
    (val: string) => {
      setView((prev) => ({
        ...prev,
        joins: (prev?.joins || []).filter(
          (p) => p !== val && !p.match(new RegExp(`JOIN ${val.match(/AS "(\w+)"/)?.[1]}(\.|IJ|LJ)`))
        ),
        selectValues: (prev?.selectValues || []).filter(
          (p) => !p.value.match(new RegExp(`"${val.match(/AS "(\w+)"/)?.[1]}("|LJ|IJ)`))
        ),
      }));

      setIsDirty(true);
    },
    [setView, setIsDirty]
  );

  const setJoin = useCallback(
    (val: string, index?: number) => {
      if (index === undefined) {
        setView((prev) => ({ ...prev, joins: [...(prev?.joins || []), val] }));
      } else {
        setView((prev) => ({
          ...prev,
          selectValues: (prev?.selectValues || []).filter(
            (p) =>
              !p.value.match(
                new RegExp(`"${prev?.joins?.[index].match(/AS "(\w+)"/)?.[1]}("|LJ|IJ)`)
              )
          ),
          joins: (prev?.joins || [])
            .map((p, i) => (i === index ? val : p))
            .filter(
              (p) =>
                !p.match(
                  new RegExp(`JOIN ${prev?.joins?.[index].match(/AS "(\w+)"/)?.[1]}(\.|IJ|LJ)`)
                )
            ),
        }));
      }
      setIsDirty(true);
    },
    [setView, setIsDirty]
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedAccordions = Array.from(view?.selectValues || []);
    const [movedItem] = reorderedAccordions.splice(result.source.index, 1);
    reorderedAccordions.splice(result.destination.index, 0, movedItem);
    setView((prev) => ({ ...prev, selectValues: reorderedAccordions }));
  };

  return (
    <Box>
      <div
        className={classes.outerContainer}
        style={{
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'row',
          padding: '22px 27px',
          gap: 20,
        }}
      >
        <div className={classes.container} style={{ flex: 8 }}>
          <div className={classes.header}>
            <div style={{ flexGrow: 1 }}>Values</div>
            <FontAwesomeIcon
              icon={faPlus}
              className={classes.icon}
              onClick={async () => {
                const value = await formDialog((props) => (
                  <SelectModal
                    selectOptions={selectOptions.filter(
                      (s) => !view?.selectValues?.some((v) => v.value === s)
                    )}
                    {...props}
                  ></SelectModal>
                ));
                setSelect(value as any);
              }}
            />
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="selects">
              {(provided) => (
                <Box
                  //@ts-ignore
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {view?.selectValues?.map((v, i) => (
                    <Draggable key={v.value} draggableId={v.value} index={i}>
                      {(provided) => (
                        <div
                          {...provided.draggableProps}
                          ref={provided.innerRef}
                          className={classes.row}
                          style={{
                            ...provided.draggableProps.style,
                            borderColor: theme.palette.background.default,
                          }}
                        >
                          <Box
                            {...provided.dragHandleProps}
                            style={{ alignContent: 'center', marginRight: 5 }}
                          >
                            <FontAwesomeIcon
                              style={{
                                width: 20,
                                height: 20,
                                color: theme.palette.text.secondary,
                              }}
                              icon={faBars}
                            />
                          </Box>
                          <div style={{ flexGrow: 1 }}>
                            <div
                              className={classes.title}
                              onClick={async () => {
                                const value = await formDialog(
                                  (props) => (
                                    <SelectModal
                                      initialLabel={v.label}
                                      initialSelect={v.value}
                                      initialLink={v.isLink}
                                      selectOptions={selectOptions.filter(
                                        (s) =>
                                          s === v.value ||
                                          !view?.selectValues?.some((v) => v.value === s)
                                      )}
                                      {...props}
                                    ></SelectModal>
                                  ),
                                  false,
                                  false,
                                  0,
                                  {}
                                );
                                setSelect(value as any, i);
                              }}
                            >
                              {v.label}
                            </div>
                            <div>{v.value}</div>
                            {!!v.isLink && (
                              <Badge
                                style={{ display: 'inline-block' }}
                                color={BadgeColors.sageGreen}
                                background={BadgeColors.sageGreen15}
                              >
                                <BadgeLabel color={BadgeColors.sageGreen}>Linked Record</BadgeLabel>
                              </Badge>
                            )}
                          </div>
                          <FontAwesomeIcon
                            icon={faTrashCan}
                            className={classes.icon}
                            onClick={() => removeSelect(i)}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <div className={classes.container} style={{ flex: 5 }}>
          <div className={classes.header}>
            <div style={{ flexGrow: 1 }}>Joins </div>
            <FontAwesomeIcon
              icon={faPlus}
              className={classes.icon}
              onClick={async () => {
                const value = await formDialog((props) => (
                  <JoinModal
                    selectOptions={joinOptions.filter((s) => !view?.joins?.some((v) => v === s))}
                    {...props}
                  ></JoinModal>
                ));
                setJoin(value as any);
              }}
            />
          </div>
          {view?.joins?.map((v, i) => (
            <div className={classes.row} style={{ borderColor: theme.palette.background.default }}>
              <div
                className={classes.title}
                onClick={async () => {
                  const value = await formDialog((props) => (
                    <JoinModal
                      initialJoin={v}
                      selectOptions={joinOptions.filter(
                        (s) =>
                          s === v ||
                          (!view?.joins?.some((v) => v === s) &&
                            !view?.joins?.some(
                              (j, idx) =>
                                idx >= i &&
                                s.match(new RegExp(`JOIN ${j.match(/AS "(\w+)"/)?.[1]}(\.|IJ|LJ)`))
                            ))
                      )}
                      {...props}
                    ></JoinModal>
                  ));
                  setJoin(value as any, i);
                }}
              >
                {v}
              </div>
              <FontAwesomeIcon
                icon={faTrashCan}
                className={classes.icon}
                onClick={() => removeJoin(v)}
              />
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    outerContainer: {
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'row',
      padding: '22px 27px',
      gap: 20,
    },
    container: {
      borderRadius: 10,
      border: '1px solid',
      display: 'flex',
      flexDirection: 'column',
      flex: 8,
      borderColor: theme.palette.background.default,
    },
    header: {
      backgroundColor: theme.palette.background.default,
      borderRadius: '10px 10px 0 0',
      padding: 10,
      display: 'flex',
      flexDirection: 'row',
    },
    title: { cursor: 'pointer', color: theme.palette.primary.main, flexGrow: 1 },
    row: { padding: 10, borderBottom: '1px solid', display: 'flex', flexDirection: 'row' },
    icon: { cursor: 'pointer', marginLeft: 10, width: 20, height: 20 },
  })
);
